/**
 * What the active tool is waiting for.
 *
 * ## Why this exists
 *
 * A tool that takes three picks gives almost no feedback between them: two points quietly
 * gain a selection halo and nothing else changes. A user who does not already know the
 * sequence cannot tell a tool that is waiting from one that is ignoring them, and reasonably
 * concludes the tool is broken — which is exactly what happened, and is what this fixes.
 *
 * The step strings were written into the tool definitions from the start and displayed
 * nowhere. Every construction tool has them; this is where they surface.
 *
 * ## Why a fixed strip and not a tooltip at the cursor
 *
 * The cursor is where the eye is, so a label following it would be read sooner. It would also
 * mean tracking the pointer through React and re-rendering on every mouse move — the one
 * thing the architecture spends real effort avoiding, for a gain measured in a few hundred
 * milliseconds of attention.
 *
 * The strip sits low and centred, directly above the camera readout, which is where a user
 * already looks for status. It appears only while a tool is waiting, so it is never furniture.
 *
 * ## Why it is charcoal rather than blue
 *
 * It was a saturated blue pill, which put the interface's *selection* colour on a transient
 * message — so a figure with a selected tool, a selected object and this strip on screen had
 * three unrelated things all shouting in the same blue. A dark neutral is what a transient
 * status reads as everywhere else, and it leaves the accent free to mean one thing. Blue
 * survives where it earns its place: the progress dots, which are the part that says *how far
 * through* rather than *pay attention*.
 */

import { radius, text } from './theme';

export interface ToolPromptProps {
  readonly tool: string;
  /** Picks already made. */
  readonly step: number;
  /** Picks needed in total. */
  readonly steps: number;
  readonly hint: string;
  /** Why the thing under the cursor cannot be used, when it cannot. */
  readonly problem?: string;
}

export function ToolPrompt({ tool, step, steps, hint, problem }: ToolPromptProps) {
  /*
   * The strip turns red and says why, rather than only the cursor refusing.
   *
   * A refusal a user cannot account for reads as a broken tool — they try the same click
   * again, then give up. Naming the reason turns it into "try the other side".
   */
  const refusing = problem !== undefined;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 84,
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 14px',
        borderRadius: radius.pill,
        background: refusing ? 'rgba(153, 27, 27, 0.94)' : 'rgba(15, 23, 42, 0.92)',
        color: '#ffffff',
        font: text.body,
        backdropFilter: 'blur(10px)',
        boxShadow: refusing
          ? '0 2px 6px rgb(15 23 42 / 12%), 0 10px 28px rgb(153 27 27 / 30%)'
          : '0 2px 6px rgb(15 23 42 / 12%), 0 10px 28px rgb(15 23 42 / 24%)',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 15,
      }}
    >
      <strong style={{ fontWeight: 600 }}>{tool}</strong>

      {/*
        Dots rather than "2 of 3". A count has to be read; three dots with two filled is
        taken in without reading, which matters for something glanced at mid-gesture.
      */}
      <span aria-hidden style={{ display: 'flex', gap: 4 }}>
        {Array.from({ length: steps }, (_, index) => (
          <span
            key={index}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: index < step ? '#60a5fa' : 'rgba(255,255,255,0.28)',
              transition: 'background var(--es-quick)',
            }}
          />
        ))}
      </span>

      <span>
        {steps > 1 && (
          <span style={{ opacity: 0.8 }}>
            {step + 1}/{steps} ·{' '}
          </span>
        )}
        {refusing ? problem : `Nhấp vào ${hint}`}
      </span>

      {step > 0 && (
        <span style={{ opacity: 0.6, fontSize: 11 }}>
          <kbd
            style={{
              padding: '1px 5px',
              borderRadius: 'var(--es-radius-xs)',
              background: 'rgba(255,255,255,0.14)',
              font: 'inherit',
            }}
          >
            Esc
          </kbd>{' '}
          để huỷ
        </span>
      )}
    </div>
  );
}
